import { useQuery } from "react-query";

import { GET_USER_TASKS, STALE_TIME_1_MINUTES } from "@constants/reactQueryKeys";
import { getUserTasks } from "api/users";
import { Task } from "api/types/users";

type UserTaskParams = {
  client_id?: number;
  type?: string;
  completed?: string;
  title?: string;
};

/**
 * @description Hook to fetch user tasks by ID using a GET request to `/v3/users/[userId]/tasks` endpoint.
 * @param {number|string} userId - The ID of the user to fetch
 * @param {?UserTaskParams} params - Object filters for the tasks.
 * @returns  An object containing user tasks, loading state, and refetch function
 */
export const useGetUserTask = (
  userId: number | string,
  params: UserTaskParams | null
): {
  tasks: Task[] | null;
  tasksLoading: boolean;
  refetchTasks: () => void;
  isTasksRefetching: boolean;
} => {
  // Keep the base key as [GET_USER_TASKS, userId, client_id] so broad callers
  // (e.g. ClientTasks, Dashboard optimistic updates) share the same cache entry.
  // Append the extra filters only when present so narrow queries (e.g. pending
  // CFQL detection) don't collide with the broad task list under the same key.
  const queryKey: (string | number | UserTaskParams | undefined)[] = [
    GET_USER_TASKS,
    userId,
    params?.client_id,
  ];
  // Append the extra filters whenever any is provided (checking for presence,
  // not truthiness, so valid falsy values like completed:false or title:"" still
  // yield a distinct key and never collide with the broad task list cache entry).
  if (
    params?.type !== undefined ||
    params?.completed !== undefined ||
    params?.title !== undefined
  ) {
    queryKey.push({
      type: params?.type,
      completed: params?.completed,
      title: params?.title,
    });
  }

  const { data, isLoading, refetch, isRefetching, ...rest } = useQuery(
    queryKey,
    () => getUserTasks(userId, params),
    {
      enabled: Boolean(userId) && Boolean(params?.client_id),
      staleTime: STALE_TIME_1_MINUTES,
      refetchInterval: STALE_TIME_1_MINUTES,
      retry: 3,
    }
  );

  return {
    tasks: data?.data || null,
    tasksLoading: isLoading,
    refetchTasks: refetch,
    isTasksRefetching: isRefetching,
    ...rest,
  };
};
